/**
 * EEHA API Service
 *
 * Frontend service for communicating with the EEHA (Electrical Equipment
 * in Hazardous Areas) inspection system API.
 */

import axios from 'axios';
import type {
  EEHAEquipment,
  EEHAInspection,
  EEHADefect,
  EEHAConfig,
  EEHADashboardStats,
  EEHAEquipmentFilters,
  EEHAInspectionFilters,
  EEHADefectFilters,
  EEHAFilterOptions,
  EEHAChecklistItemTemplate,
  EEHATag,
  EEHASiteMap,
  GeoCoordinates,
  EEHALot,
  EEHALotFilters,
  EEHALotComplianceStats,
  EEHALotCriteria,
  EEHASamplingStrategy,
  EEHARiskAssessment,
  EEHARiskAssessmentFilters,
  EEHARiskFactors,
  RiskCalculationResult,
  RiskRating,
  AssessmentType,
  EEHASample,
  EEHASampleFilters,
  SampleGenerationOptions,
  SampleProgress,
  SampleType,
  InspectionResult,
  EEHAFaultCategory,
  EEHAWorkOrderLink,
  EEHACampaign,
  EEHACampaignFilters,
  EEHAInspectionApprover,
  GanttForecastFilters,
  GanttForecastData,
  EEHAResolvedConfig,
  EEHAScopedConfig,
  EEHAScopedChangeEntry,
  EEHAWorkpack,
  EEHAEnrichedWorkpack,
  EEHAWorkpackFilters,
  WorkpackDownloadBundle,
  WorkpackSourceType,
  WorkpackStatus,
  SyncStatus,
  EEHAOfflineQueueItem,
  EEHAOfflineSyncStatus,
  EEHASyncBatchResult,
  EEHASyncBatchSummary,
  OfflineEntityType,
  OfflineOperation,
  ConflictResolution,
} from '../types/eeha';

// ============================================================================
// Helpers
// ============================================================================


function buildQueryString(params: Record<string, unknown> | undefined): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.append(key, value.join(','));
      }
    } else if (typeof value === 'boolean') {
      searchParams.append(key, String(value));
    } else {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

// ============================================================================
// Equipment API
// ============================================================================

export async function getEquipmentList(
  filters?: EEHAEquipmentFilters
): Promise<{ equipment: EEHAEquipment[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    zoneClassification: filters?.zoneClassification,
    status: filters?.status,
    overdue: filters?.overdue,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/equipment${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get equipment list');
  }

  return response.data.data;
}

export async function getEquipment(id: string): Promise<EEHAEquipment> {
  const response = await axios.get(`/api/eeha/equipment/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get equipment');
  }

  return response.data.data;
}

export async function createEquipment(data: Partial<EEHAEquipment>): Promise<EEHAEquipment> {
  const response = await axios.post(`/api/eeha/equipment`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create equipment');
  }

  return response.data.data;
}

export async function updateEquipment(id: string, data: Partial<EEHAEquipment>): Promise<EEHAEquipment> {
  const response = await axios.put(`/api/eeha/equipment/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update equipment');
  }

  return response.data.data;
}

export async function deleteEquipment(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/equipment/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete equipment');
  }
}

export async function generateEquipmentQR(id: string): Promise<{ qrId: string; qrDataUrl: string }> {
  const response = await axios.post(`/api/eeha/equipment/${id}/qr`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to generate QR code');
  }

  return response.data.data;
}

export async function scanEquipmentQR(code: string): Promise<{ equipmentId: string }> {
  const response = await axios.get(`/api/eeha/equipment/scan?code=${encodeURIComponent(code)}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to scan QR code');
  }

  return response.data.data;
}

export async function getEquipmentStats(): Promise<{
  totalEquipment: number;
  activeEquipment: number;
  overdueInspections: number;
  upcomingInspections: Array<{
    equipmentId: string;
    equipmentNo: string;
    currentTagNo: string;
    nextInspectionDue: string;
  }>;
}> {
  const response = await axios.get(`/api/eeha/equipment/stats`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get equipment stats');
  }

  return response.data.data;
}

export async function getEquipmentInspections(equipmentId: string): Promise<EEHAInspection[]> {
  const response = await axios.get(`/api/eeha/equipment/${equipmentId}/inspections`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get equipment inspections');
  }

  return response.data.data;
}

export async function getEquipmentDefects(equipmentId: string): Promise<EEHADefect[]> {
  const response = await axios.get(`/api/eeha/equipment/${equipmentId}/defects`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get equipment defects');
  }

  return response.data.data;
}

export async function getEquipmentFilterOptions(): Promise<EEHAFilterOptions> {
  const response = await axios.get(`/api/eeha/equipment/filter-options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get filter options');
  }

  return response.data.data;
}

// ============================================================================
// Equipment Document API
// ============================================================================

export async function uploadEquipmentDocument(
  equipmentId: string,
  file: File,
  metadata: {
    title: string;
    category: string;
    documentNumber?: string;
    description?: string;
    expiryDate?: string;
    tags?: string[];
  }
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', metadata.title);
  formData.append('category', metadata.category);
  if (metadata.documentNumber) formData.append('documentNumber', metadata.documentNumber);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
  if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

  const response = await axios.post(`/api/eeha/equipment/${equipmentId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to upload document');
  }

  return response.data.data;
}

export async function deleteEquipmentDocument(equipmentId: string, documentId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/equipment/${equipmentId}/documents/${documentId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete document');
  }
}

export function getEquipmentDocumentDownloadUrl(equipmentId: string, documentId: string): string {
  return `/api/eeha/equipment/${equipmentId}/documents/${documentId}/download`;
}

// ============================================================================
// Inspection API
// ============================================================================

export async function getInspectionList(
  filters?: EEHAInspectionFilters
): Promise<{ inspections: EEHAInspection[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    equipmentId: filters?.equipmentId,
    tagId: filters?.tagId,
    tagNo: filters?.tagNo,
    inspectionType: filters?.inspectionType,
    status: filters?.status,
    overallResult: filters?.overallResult,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/inspections${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get inspection list');
  }

  return response.data.data;
}

export async function getInspection(id: string): Promise<EEHAInspection> {
  const response = await axios.get(`/api/eeha/inspections/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get inspection');
  }

  return response.data.data;
}

export async function createInspection(data: Partial<EEHAInspection>): Promise<EEHAInspection & { _warnings?: string[] }> {
  const response = await axios.post(`/api/eeha/inspections`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create inspection');
  }

  const result = response.data.data;
  if (response.data.warnings) {
    result._warnings = response.data.warnings;
  }
  return result;
}

export async function updateInspection(id: string, data: Partial<EEHAInspection>): Promise<EEHAInspection> {
  const response = await axios.put(`/api/eeha/inspections/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update inspection');
  }

  return response.data.data;
}

export async function deleteInspection(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/inspections/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete inspection');
  }
}

export async function submitInspection(
  id: string,
  data?: { selectedApproverEmails?: string[] }
): Promise<EEHAInspection> {
  const response = await axios.post(`/api/eeha/inspections/${id}/submit`, data || {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to submit inspection');
  }

  return response.data.data;
}

export async function approveInspection(
  id: string,
  data: { signatureData?: string }
): Promise<EEHAInspection> {
  const response = await axios.post(`/api/eeha/inspections/${id}/approve`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to approve inspection');
  }

  return response.data.data;
}

export async function rejectInspection(
  id: string,
  data: { reason: string }
): Promise<EEHAInspection> {
  const response = await axios.post(`/api/eeha/inspections/${id}/reject`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to reject inspection');
  }

  return response.data.data;
}

export async function uploadInspectionFile(
  id: string,
  file: File,
  type: 'photo' | 'attachment' | 'signature',
  caption?: string
): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (caption) formData.append('caption', caption);

  const response = await axios.post(`/api/eeha/inspections/${id}/files`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to upload file');
  }

  return response.data.data;
}

export async function deleteInspectionFile(id: string, fileId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/inspections/${id}/files/${fileId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete file');
  }
}

export async function renderInspectionPdf(id: string): Promise<Blob> {
  const response = await axios.get(`/api/eeha/inspections/${id}/render`, {
    responseType: 'blob',
  });

  return response.data;
}

export async function getInspectionDashboardStats(
  clientName?: string,
  siteName?: string,
  plantName?: string
): Promise<EEHADashboardStats> {
  const params = new URLSearchParams();
  if (clientName) params.append('clientName', clientName);
  if (siteName) params.append('siteName', siteName);
  if (plantName) params.append('plantName', plantName);
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  const response = await axios.get(`/api/eeha/inspections/stats/dashboard${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get dashboard stats');
  }

  return response.data.data;
}

export async function getInspectionForecastGantt(
  filters?: GanttForecastFilters
): Promise<GanttForecastData> {
  const params = new URLSearchParams();
  if (filters?.clientName) params.append('clientName', filters.clientName);
  if (filters?.siteName) params.append('siteName', filters.siteName);
  if (filters?.plantName) params.append('plantName', filters.plantName);
  if (filters?.daysAhead) params.append('daysAhead', String(filters.daysAhead));
  if (filters?.includeOverdue !== undefined) params.append('includeOverdue', String(filters.includeOverdue));
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  const response = await axios.get(`/api/eeha/inspections/forecast/gantt${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get forecast gantt data');
  }

  return response.data.data;
}

export async function getInspectionFilterOptions(): Promise<EEHAFilterOptions> {
  const response = await axios.get(`/api/eeha/inspections/filter-options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get filter options');
  }

  return response.data.data;
}

// ============================================================================
// Defect API
// ============================================================================

export async function getDefectList(
  filters?: EEHADefectFilters
): Promise<{ defects: EEHADefect[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    equipmentId: filters?.equipmentId,
    tagId: filters?.tagId,
    tagNo: filters?.tagNo,
    inspectionId: filters?.inspectionId,
    severity: filters?.severity,
    status: filters?.status,
    category: filters?.category,
    protectionType: filters?.protectionType,
    isOverdue: filters?.isOverdue,
    assignedTo: filters?.assignedTo,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/defects${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get defect list');
  }

  return response.data.data;
}

export async function getDefect(id: string): Promise<EEHADefect> {
  const response = await axios.get(`/api/eeha/defects/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get defect');
  }

  return response.data.data;
}

export async function createDefect(data: Partial<EEHADefect>): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create defect');
  }

  return response.data.data;
}

export async function updateDefect(id: string, data: Partial<EEHADefect>): Promise<EEHADefect> {
  const response = await axios.put(`/api/eeha/defects/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update defect');
  }

  return response.data.data;
}

export async function assignDefect(
  id: string,
  data: { assignedTo: string; assignedToEmail?: string; note?: string }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/assign`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to assign defect');
  }

  return response.data.data;
}

export async function remediateDefect(
  id: string,
  data: { action: string; comments?: string; partsUsed?: string; workOrderNo?: string }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/remediate`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to remediate defect');
  }

  return response.data.data;
}

export async function verifyDefect(
  id: string,
  data: { result: 'Pass' | 'Fail' | 'Requires Further Work'; comments?: string; signatureData?: string }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/verify`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to verify defect');
  }

  return response.data.data;
}

export async function closeDefect(id: string, closureNotes?: string): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/close`, { closureNotes });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to close defect');
  }

  return response.data.data;
}

export async function deferDefect(
  id: string,
  data: { reason: string; deferredUntil?: string }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/defer`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to defer defect');
  }

  return response.data.data;
}

// New workflow actions

export async function submitDefectForReview(id: string): Promise<EEHADefect> {
  const response = await axios.post(
    `/api/eeha/defects/${id}/submit-for-review`,
    {}
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to submit for review');
  }

  return response.data.data;
}

export async function completeDefectReview(
  id: string,
  data: {
    severity?: string;
    category?: string;
    description?: string;
    recommendedAction?: string;
    notes?: string;
  }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/complete-review`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to complete review');
  }

  return response.data.data;
}

export async function linkDefectWorkOrder(
  id: string,
  data: {
    workOrderNo?: string;
    workOrderLink?: string;
    workOrderNotes?: string;
    assignedTo?: string;
  }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/link-work-order`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to link work order');
  }

  return response.data.data;
}

export async function resolveDefect(
  id: string,
  data: {
    actionTaken?: string;
    resolutionNotes?: string;
    partsUsed?: string;
  }
): Promise<EEHADefect> {
  const response = await axios.post(`/api/eeha/defects/${id}/resolve`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to resolve defect');
  }

  return response.data.data;
}

export async function uploadDefectPhoto(
  id: string,
  file: File,
  type: 'defect' | 'remediation',
  caption?: string
): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (caption) formData.append('caption', caption);

  const response = await axios.post(`/api/eeha/defects/${id}/photos`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to upload photo');
  }

  return response.data.data;
}

export async function deleteDefectPhoto(id: string, fileId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/defects/${id}/photos/${fileId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete photo');
  }
}

export async function getOverdueDefects(clientName?: string): Promise<EEHADefect[]> {
  const queryParams = clientName ? `?clientName=${encodeURIComponent(clientName)}` : '';

  const response = await axios.get(`/api/eeha/defects/overdue${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get overdue defects');
  }

  return response.data.data;
}

export async function getDefectSummary(clientName?: string): Promise<{
  bySeverity: Record<string, { count: number; overdue: number }>;
  byZone: Record<string, { count: number; critical: number; major: number; minor: number }>;
  byCategory: Record<string, number>;
  byStatus: Record<string, number>;
}> {
  const queryParams = clientName ? `?clientName=${encodeURIComponent(clientName)}` : '';

  const response = await axios.get(`/api/eeha/defects/summary${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get defect summary');
  }

  return response.data.data;
}

export async function getDefectFilterOptions(): Promise<EEHAFilterOptions> {
  const response = await axios.get(`/api/eeha/defects/filter-options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get filter options');
  }

  return response.data.data;
}

// ============================================================================
// Config API
// ============================================================================

export async function getConfig(): Promise<EEHAConfig> {
  const response = await axios.get(`/api/eeha-config`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get config');
  }

  return response.data.data;
}

export async function updateConfig(data: Partial<EEHAConfig>): Promise<EEHAConfig> {
  const response = await axios.put(`/api/eeha-config`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update config');
  }

  return response.data.data;
}

export async function getDefaultChecklist(): Promise<EEHAChecklistItemTemplate[]> {
  const response = await axios.get(`/api/eeha-config/default-checklist`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get default checklist');
  }

  return response.data.data;
}

export async function getConfigOptions(): Promise<EEHAConfig['options']> {
  const response = await axios.get(`/api/eeha-config/options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get config options');
  }

  return response.data.data;
}

export async function getSLADefaults(): Promise<EEHAConfig['slaDefaults']> {
  const response = await axios.get(`/api/eeha-config/sla-defaults`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get SLA defaults');
  }

  return response.data.data;
}

export async function getClientSiteHierarchy(): Promise<
  Array<{ client: string; sites: Array<{ name: string; plants?: string[] }> }>
> {
  const response = await axios.get(`/api/eeha-config/clients`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get client/site hierarchy');
  }

  return response.data.data;
}

// ============================================================================
// User Permissions API
// ============================================================================

export interface EEHAClientPermission {
  name: string;
  permission: 'readonly' | 'edit' | 'admin';
}

export interface EEHAUserPermissions {
  isAdmin: boolean;
  clients: EEHAClientPermission[];
}

export async function getMyPermissions(): Promise<EEHAUserPermissions> {
  const response = await axios.get(`/api/eeha-config/my-permissions`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get user permissions');
  }

  return response.data.data;
}

// ============================================================================
// Client Config API
// ============================================================================

export interface EEHAClientConfig {
  clientName: string;
  displayName?: string;
  contacts: Array<{
    _id?: string;
    name: string;
    email: string;
    phone?: string;
    role?: string;
    receiveDigest?: boolean;
    receiveAlerts?: boolean;
  }>;
  roleRecipients: Array<{
    role: string;
    emails: string[];
  }>;
  notificationRules: {
    defectCreated?: boolean;
    defectEscalated?: boolean;
    inspectionDue?: boolean;
    inspectionOverdue?: boolean;
    inspectionApproved?: boolean;
    inspectionRejected?: boolean;
    digestFrequency?: 'daily' | 'weekly' | 'monthly';
  };
  siteOverrides: Array<{
    siteName: string;
    contacts?: Array<{ name: string; email: string; role?: string }>;
    notificationRules?: Record<string, boolean>;
  }>;
  settings: {
    timezone?: string;
    digestTime?: string;
    includeEquipmentSummary?: boolean;
    includeDefectSummary?: boolean;
    includeInspectionSummary?: boolean;
  };
  features: {
    notificationsEnabled?: boolean;
    digestEnabled?: boolean;
    externalPortalEnabled?: boolean;
  };
  updatedAt?: string;
  updatedBy?: string;
}

export async function getClientConfig(clientName: string): Promise<EEHAClientConfig> {
  const response = await axios.get(`/api/eeha-config/notifications/${encodeURIComponent(clientName)}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get client config');
  }

  return response.data.data;
}

export async function updateClientConfig(
  clientName: string,
  data: Partial<EEHAClientConfig>
): Promise<EEHAClientConfig> {
  const response = await axios.put(`/api/eeha-config/notifications/${encodeURIComponent(clientName)}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update client config');
  }

  return response.data.data;
}

export async function addClientContact(
  clientName: string,
  contact: { name: string; email: string; phone?: string; role?: string; receiveDigest?: boolean; receiveAlerts?: boolean }
): Promise<EEHAClientConfig['contacts']> {
  const response = await axios.post(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/contacts`,
    contact
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add contact');
  }

  return response.data.data;
}

export async function updateClientContact(
  clientName: string,
  contactId: string,
  data: Partial<EEHAClientConfig['contacts'][0]>
): Promise<EEHAClientConfig['contacts']> {
  const response = await axios.put(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/contacts/${contactId}`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update contact');
  }

  return response.data.data;
}

export async function deleteClientContact(
  clientName: string,
  contactId: string
): Promise<EEHAClientConfig['contacts']> {
  const response = await axios.delete(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/contacts/${contactId}`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete contact');
  }

  return response.data.data;
}

export async function addSiteOverride(
  clientName: string,
  siteOverride: EEHAClientConfig['siteOverrides'][0]
): Promise<EEHAClientConfig['siteOverrides']> {
  const response = await axios.post(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/sites`,
    siteOverride
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add site override');
  }

  return response.data.data;
}

export async function updateSiteOverride(
  clientName: string,
  siteName: string,
  data: Partial<EEHAClientConfig['siteOverrides'][0]>
): Promise<EEHAClientConfig['siteOverrides']> {
  const response = await axios.put(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/sites/${encodeURIComponent(siteName)}`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update site override');
  }

  return response.data.data;
}

export async function deleteSiteOverride(
  clientName: string,
  siteName: string
): Promise<EEHAClientConfig['siteOverrides']> {
  const response = await axios.delete(
    `/api/eeha-config/notifications/${encodeURIComponent(clientName)}/sites/${encodeURIComponent(siteName)}`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete site override');
  }

  return response.data.data;
}

// ============================================================================
// Google Maps Config
// ============================================================================

export interface GoogleMapsConfigResponse {
  enabled: boolean;
  apiKey: string | null;
  defaultMapType: 'satellite' | 'hybrid' | 'roadmap' | 'terrain';
  defaultZoom: number;
  configured: boolean;
  hasDbKey?: boolean;
}

export async function getGoogleMapsConfig(): Promise<GoogleMapsConfigResponse> {
  const response = await axios.get(`/api/eeha-config/google-maps`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get Google Maps config');
  }

  return response.data.data;
}

export async function updateGoogleMapsConfig(data: {
  enabled?: boolean;
  defaultMapType?: 'satellite' | 'hybrid' | 'roadmap' | 'terrain';
  defaultZoom?: number;
  apiKey?: string;
}): Promise<void> {
  const response = await axios.put(`/api/eeha-config/google-maps`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update Google Maps config');
  }
}

// ============================================================================
// Inspection Approvers API
// ============================================================================

export async function getClientInspectionApprovers(
  clientName: string
): Promise<EEHAInspectionApprover[]> {
  const response = await axios.get(
    `/api/eeha-config/inspection-approvers/${encodeURIComponent(clientName)}`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get inspection approvers');
  }

  return response.data.data;
}

export async function getEligibleApprovers(
  clientName: string,
  siteName: string,
  plantName?: string
): Promise<EEHAInspectionApprover[]> {
  const params = buildQueryString({ siteName, plantName });
  const response = await axios.get(
    `/api/eeha-config/inspection-approvers/${encodeURIComponent(clientName)}/eligible${params}`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get eligible approvers');
  }

  return response.data.data;
}

export async function addClientInspectionApprover(
  clientName: string,
  data: Omit<EEHAInspectionApprover, '_id'>
): Promise<EEHAInspectionApprover[]> {
  const response = await axios.post(
    `/api/eeha-config/inspection-approvers/${encodeURIComponent(clientName)}`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add inspection approver');
  }

  return response.data.data;
}

export async function updateClientInspectionApprover(
  clientName: string,
  approverId: string,
  data: Partial<EEHAInspectionApprover>
): Promise<EEHAInspectionApprover[]> {
  const response = await axios.put(
    `/api/eeha-config/inspection-approvers/${encodeURIComponent(clientName)}/${approverId}`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update inspection approver');
  }

  return response.data.data;
}

export async function deleteClientInspectionApprover(
  clientName: string,
  approverId: string
): Promise<EEHAInspectionApprover[]> {
  const response = await axios.delete(
    `/api/eeha-config/inspection-approvers/${encodeURIComponent(clientName)}/${approverId}`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete inspection approver');
  }

  return response.data.data;
}

// ============================================================================
// Utility Functions
// ============================================================================

export function getSeverityColor(severity: string): string {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return 'red';
    case 'major':
      return 'amber';
    case 'minor':
      return 'blue';
    default:
      return 'gray';
  }
}

export function getStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'draft':
      return 'gray';
    case 'inprogress':
      return 'blue';
    case 'pendingapproval':
    case 'remediated':
      return 'amber';
    case 'approved':
    case 'verified':
    case 'closed':
      return 'green';
    case 'rejected':
    case 'open':
      return 'red';
    case 'assigned':
      return 'purple';
    case 'deferred':
      return 'orange';
    default:
      return 'gray';
  }
}

export function getResultColor(result: string): string {
  switch (result?.toLowerCase()) {
    case 'pass':
      return 'green';
    case 'fail':
      return 'red';
    case 'conditional pass':
      return 'amber';
    case 'n/a':
    case 'not inspected':
      return 'gray';
    default:
      return 'gray';
  }
}

export function getZoneDescription(zone: string): string {
  const descriptions: Record<string, string> = {
    'Zone 0': 'Explosive gas atmosphere continuously present',
    'Zone 1': 'Explosive gas atmosphere likely in normal operation',
    'Zone 2': 'Explosive gas atmosphere unlikely in normal operation',
    'Zone 20': 'Combustible dust cloud continuously present',
    'Zone 21': 'Combustible dust cloud likely in normal operation',
    'Zone 22': 'Combustible dust cloud unlikely in normal operation',
  };
  return descriptions[zone] || zone;
}

export function formatSLADays(days: number): string {
  if (days === 1) return '1 day';
  return `${days} days`;
}

export function calculateDaysUntilDue(dueDate: string | Date): number {
  const due = new Date(dueDate);
  const now = new Date();
  const diffTime = due.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// ============================================================================
// Tag API (NEW - Location Registry)
// ============================================================================

// Re-export EEHATag from types for backwards compatibility
export type { EEHATag };

export interface EEHATagFilters {
  search?: string;
  clientName?: string;
  siteName?: string;
  plantName?: string;
  zoneClassification?: string;
  status?: string;
  hasEquipment?: boolean;
  overdueOnly?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export async function getTagList(
  filters?: EEHATagFilters
): Promise<{ tags: EEHATag[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    zoneClassification: filters?.zoneClassification,
    status: filters?.status,
    hasEquipment: filters?.hasEquipment,
    overdueOnly: filters?.overdueOnly,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha-tags${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get tag list');
  }

  return response.data.data;
}

export async function getTag(id: string): Promise<EEHATag> {
  const response = await axios.get(`/api/eeha-tags/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get tag');
  }

  return response.data.data;
}

export async function createTag(data: Partial<EEHATag>): Promise<EEHATag> {
  const response = await axios.post(`/api/eeha-tags`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create tag');
  }

  return response.data.data;
}

export async function updateTag(id: string, data: Partial<EEHATag>): Promise<EEHATag> {
  const response = await axios.put(`/api/eeha-tags/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update tag');
  }

  return response.data.data;
}

export async function deleteTag(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha-tags/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete tag');
  }
}

export async function assignEquipmentToTag(
  tagId: string,
  data: { serialEquipmentId: string; reason?: string; workOrderNo?: string }
): Promise<EEHATag> {
  const response = await axios.post(`/api/eeha-tags/${tagId}/assign-equipment`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to assign equipment');
  }

  return response.data.data;
}

export async function removeEquipmentFromTag(
  tagId: string,
  data: { reason?: string; storageLocation?: string }
): Promise<EEHATag> {
  const response = await axios.post(`/api/eeha-tags/${tagId}/remove-equipment`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to remove equipment');
  }

  return response.data.data;
}

export async function generateTagQR(id: string): Promise<{ qr: EEHATag['qr'] }> {
  const response = await axios.post(`/api/eeha-tags/${id}/generate-qr`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to generate QR code');
  }

  return response.data.data;
}

export async function scanTagQR(qrId: string): Promise<{ tag: EEHATag; equipment?: unknown }> {
  const response = await axios.get(`/api/eeha-tags/scan/${qrId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to scan QR code');
  }

  return response.data.data;
}

export async function getTagHistory(id: string): Promise<unknown[]> {
  const response = await axios.get(`/api/eeha-tags/${id}/history`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get tag history');
  }

  return response.data.data;
}

export async function getTagFilterOptions(): Promise<EEHAFilterOptions> {
  const response = await axios.get(`/api/eeha-tags/filter-options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get filter options');
  }

  return response.data.data;
}

export async function assignScheduleToTags(data: {
  tagIds: string[];
  frequencyMonths: number;
  inspectionGrade: string;
  assessmentId?: string;
  assessmentNo?: string;
}): Promise<{ updatedCount: number; errors?: Array<{ tagId: string; error: string }> }> {
  const response = await axios.post('/api/eeha-tags/assign-schedule', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to assign schedule');
  }

  return response.data.data;
}

// ============================================================================
// Site Map API (NEW - Map Management)
// ============================================================================

// EEHASiteMap type is imported from '../types/eeha'

export async function getSiteMapList(filters?: {
  clientName?: string;
  siteName?: string;
}): Promise<EEHASiteMap[]> {
  const queryParams = buildQueryString({
    clientName: filters?.clientName,
    siteName: filters?.siteName,
  });

  const response = await axios.get(`/api/eeha-site-maps${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get site map list');
  }

  return response.data.data;
}

export async function getSiteMapsBySite(clientName: string, siteName: string): Promise<EEHASiteMap[]> {
  const response = await axios.get(`/api/eeha-site-maps/by-site/${encodeURIComponent(clientName)}/${encodeURIComponent(siteName)}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get site maps');
  }

  return response.data.data;
}

export async function getSiteMap(id: string): Promise<EEHASiteMap> {
  const response = await axios.get(`/api/eeha-site-maps/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get site map');
  }

  return response.data.data;
}

export async function getSiteMapWithTags(id: string): Promise<EEHASiteMap> {
  const response = await axios.get(`/api/eeha-site-maps/${id}/with-tags`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get site map with tags');
  }

  return response.data.data;
}

export async function createSiteMap(data: {
  clientName: string;
  siteName: string;
  name: string;
  description?: string;
  plantName?: string;
  mapSource?: 'image' | 'google';
  mapType?: string;
  fileData?: string;
  originalFileName?: string;
  googleMapsConfig?: {
    center: { lat: number; lng: number };
    zoom: number;
    mapTypeId: 'satellite' | 'hybrid' | 'roadmap' | 'terrain';
  };
  order?: number;
}): Promise<EEHASiteMap> {
  const response = await axios.post(`/api/eeha-site-maps`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create site map');
  }

  return response.data.data;
}

export async function updateSiteMap(id: string, data: Partial<EEHASiteMap>): Promise<EEHASiteMap> {
  const response = await axios.put(`/api/eeha-site-maps/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update site map');
  }

  return response.data.data;
}

export async function updateSiteMapFile(
  id: string,
  data: { fileData: string; mapType: string; originalFileName?: string; dimensions?: unknown }
): Promise<void> {
  const response = await axios.put(`/api/eeha-site-maps/${id}/file`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update site map file');
  }
}

export async function deleteSiteMap(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha-site-maps/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete site map');
  }
}

export async function addTagToMap(
  mapId: string,
  data: { tagId: string; x?: number; y?: number; label?: string; coordinates?: GeoCoordinates }
): Promise<unknown[]> {
  const response = await axios.post(`/api/eeha-site-maps/${mapId}/tags`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add tag to map');
  }

  return response.data.data;
}

export async function updateTagPositionOnMap(
  mapId: string,
  tagId: string,
  data: { x?: number; y?: number; coordinates?: GeoCoordinates }
): Promise<unknown[]> {
  const response = await axios.put(`/api/eeha-site-maps/${mapId}/tags/${tagId}/position`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update tag position');
  }

  return response.data.data;
}

export async function removeTagFromMap(mapId: string, tagId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha-site-maps/${mapId}/tags/${tagId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to remove tag from map');
  }
}

export async function addZoneToMap(
  mapId: string,
  data: {
    name: string;
    zoneClass: string;
    polygon?: Array<{ x: number; y: number }>;
    geoPolygon?: GeoCoordinates[];
    color?: string;
    opacity?: number;
    description?: string;
  }
): Promise<unknown[]> {
  const response = await axios.post(`/api/eeha-site-maps/${mapId}/zones`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add zone to map');
  }

  return response.data.data;
}

export async function updateZoneOnMap(
  mapId: string,
  zoneId: string,
  data: Partial<{
    name: string;
    zoneClass: string;
    polygon: Array<{ x: number; y: number }>;
    color?: string;
    opacity?: number;
    description?: string;
  }>
): Promise<unknown[]> {
  const response = await axios.put(`/api/eeha-site-maps/${mapId}/zones/${zoneId}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update zone');
  }

  return response.data.data;
}

export async function deleteZoneFromMap(mapId: string, zoneId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha-site-maps/${mapId}/zones/${zoneId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete zone');
  }
}

// ============================================================================
// Site Map Labels (Area navigational waypoints)
// ============================================================================

export async function addLabelToMap(
  mapId: string,
  data: {
    name: string;
    coordinates: GeoCoordinates;
    zoomLevel?: number;
    color?: string;
    icon?: string;
    plantName?: string;
    linkedMapId?: string;
  }
): Promise<unknown[]> {
  const response = await axios.post(`/api/eeha-site-maps/${mapId}/labels`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add label to map');
  }

  return response.data.data;
}

export async function updateLabelOnMap(
  mapId: string,
  labelId: string,
  data: Partial<{
    name: string;
    coordinates: GeoCoordinates;
    zoomLevel: number;
    color: string;
    icon: string;
    plantName: string;
    linkedMapId: string;
  }>
): Promise<unknown[]> {
  const response = await axios.put(`/api/eeha-site-maps/${mapId}/labels/${labelId}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update label');
  }

  return response.data.data;
}

export async function deleteLabelFromMap(mapId: string, labelId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha-site-maps/${mapId}/labels/${labelId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete label');
  }
}

// Traffic Light Helpers
export function getTrafficLightColor(status: 'green' | 'yellow' | 'red' | 'gray'): string {
  const colors = {
    green: '#22c55e',
    yellow: '#eab308',
    red: '#ef4444',
    gray: '#9ca3af',
  };
  return colors[status] || colors.gray;
}

export function getTrafficLightDescription(status: 'green' | 'yellow' | 'red' | 'gray'): string {
  const descriptions = {
    green: 'Compliant - no defects, inspection up to date',
    yellow: 'Due soon - inspection due within 30 days',
    red: 'Action required - has open defect or overdue inspection',
    gray: 'No history - requires Initial Detailed inspection',
  };
  return descriptions[status] || 'Unknown status';
}

// ============================================================================
// Tag Bulk Import Functions
// ============================================================================

export interface BulkImportResult {
  total: number;
  created: number;
  skipped: number;
  errorCount: number;
  errors: Array<{ row: number; tagNo?: string; error: string }>;
  createdTags: string[];
}

export async function bulkImportTags(
  file: File,
  clientName: string,
  siteName: string
): Promise<{ success: boolean; data: BulkImportResult; message: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('clientName', clientName);
  formData.append('siteName', siteName);

  const response = await axios.post('/api/eeha/tags/bulk-import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function downloadTagImportTemplate(): Promise<Blob> {
  const response = await axios.get('/api/eeha/tags/import-template', {
    responseType: 'blob',
  });
  return response.data;
}

// ============================================================================
// Lot API (NEW - Risk-Based Inspection Grouping)
// ============================================================================

// Re-export lot types for convenience
export type { EEHALot, EEHALotFilters, EEHALotComplianceStats, EEHALotCriteria, EEHASamplingStrategy };

export interface LotFilterOptions {
  systemNos: string[];
  subSystemNos: string[];
  packageNos: string[];
  zoneClassifications: string[];
  criticalities: string[];
  equipmentTypes: string[];
  protectionMethods: string[];
  riskCategories: string[];
  statuses: string[];
}

export async function getLotList(
  filters?: EEHALotFilters
): Promise<{ lots: EEHALot[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    riskCategory: filters?.riskCategory,
    status: filters?.status,
    membershipType: filters?.membershipType,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/lots${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot list');
  }

  return response.data.data;
}

export async function getLot(id: string): Promise<EEHALot> {
  const response = await axios.get(`/api/eeha/lots/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot');
  }

  return response.data.data;
}

export async function createLot(data: {
  clientName: string;
  siteName: string;
  plantName?: string;
  name: string;
  description?: string;
  membershipType: 'automatic' | 'manual';
  criteria?: EEHALotCriteria;
  tagIds?: string[];
  riskScore?: number;
  riskCategory?: 'High' | 'Medium' | 'Low';
  riskNotes?: string;
  samplingStrategy?: Partial<EEHASamplingStrategy>;
}): Promise<EEHALot> {
  const response = await axios.post(`/api/eeha/lots`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create lot');
  }

  return response.data.data;
}

export async function updateLot(id: string, data: Partial<EEHALot>): Promise<EEHALot> {
  const response = await axios.put(`/api/eeha/lots/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update lot');
  }

  return response.data.data;
}

export async function deleteLot(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/lots/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete lot');
  }
}

export async function getLotTags(
  id: string,
  page?: number,
  limit?: number
): Promise<{ tags: EEHATag[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({ page, limit });

  const response = await axios.get(`/api/eeha/lots/${id}/tags${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot tags');
  }

  return response.data.data;
}

export async function addTagsToLot(id: string, tagIds: string[]): Promise<{ addedCount: number; alreadyInLot: number }> {
  const response = await axios.post(`/api/eeha/lots/${id}/tags`, { tagIds });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add tags to lot');
  }

  return response.data.data;
}

export async function removeTagFromLot(id: string, tagId: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/lots/${id}/tags/${tagId}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to remove tag from lot');
  }
}

export async function getLotCompliance(id: string): Promise<EEHALotComplianceStats> {
  const response = await axios.get(`/api/eeha/lots/${id}/compliance`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot compliance');
  }

  return response.data.data;
}

export async function recalculateLotStats(id: string): Promise<EEHALot['stats']> {
  const response = await axios.post(`/api/eeha/lots/${id}/recalculate`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to recalculate lot stats');
  }

  return response.data.data;
}

export async function getLotFilterOptions(): Promise<LotFilterOptions> {
  const response = await axios.get(`/api/eeha/lots/filter-options`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot filter options');
  }

  return response.data.data;
}

export interface LotCriteriaOptions {
  zoneClassifications: string[];
  systemNos: string[];
  subSystemNos: string[];
  packageNos: string[];
  criticalities: string[];
  equipmentTypes: string[];
  protectionMethods: string[];
}

export async function getLotCriteriaOptions(clientName: string, siteName?: string): Promise<LotCriteriaOptions> {
  const params: Record<string, string> = { clientName };
  if (siteName) params.siteName = siteName;

  const response = await axios.get(`/api/eeha/lots/criteria-options`, {
    params,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get lot criteria options');
  }

  return response.data.data;
}

// Lot Risk Category helpers
export function getRiskCategoryColor(category: 'High' | 'Medium' | 'Low'): string {
  const colors = {
    High: '#ef4444',    // red
    Medium: '#f59e0b',  // amber
    Low: '#22c55e',     // green
  };
  return colors[category] || '#9ca3af';
}

export function getRiskCategoryBgColor(category: 'High' | 'Medium' | 'Low'): string {
  const colors = {
    High: '#fef2f2',    // red-50
    Medium: '#fffbeb',  // amber-50
    Low: '#f0fdf4',     // green-50
  };
  return colors[category] || '#f9fafb';
}

export function getMembershipTypeLabel(type: 'automatic' | 'manual'): string {
  return type === 'automatic' ? 'Criteria-Based' : 'Manual Selection';
}

// ============================================================================
// Risk Assessment API (RBI - Risk-Based Inspection)
// ============================================================================

export async function getRiskAssessmentList(
  filters?: EEHARiskAssessmentFilters
): Promise<{ assessments: EEHARiskAssessment[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    lotId: filters?.lotId,
    tagId: filters?.tagId,
    assessmentType: filters?.assessmentType,
    status: filters?.status,
    riskRating: filters?.riskRating,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/risk-assessments${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get risk assessments');
  }

  return response.data.data;
}

export async function getRiskAssessment(id: string): Promise<EEHARiskAssessment> {
  const response = await axios.get(`/api/eeha/risk-assessments/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get risk assessment');
  }

  return response.data.data;
}

export async function createRiskAssessment(data: {
  clientName: string;
  siteName: string;
  plantName?: string;
  lotId?: string;
  tagId?: string;
  tagIds?: string[];
  tagNos?: string[];
  assessmentType: AssessmentType;
  factors: EEHARiskFactors;
}): Promise<EEHARiskAssessment> {
  const response = await axios.post('/api/eeha/risk-assessments', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create risk assessment');
  }

  return response.data.data;
}

export async function updateRiskAssessment(
  id: string,
  data: Partial<{
    assessmentType: AssessmentType;
    factors: Partial<EEHARiskFactors>;
  }>
): Promise<EEHARiskAssessment> {
  const response = await axios.put(`/api/eeha/risk-assessments/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update risk assessment');
  }

  return response.data.data;
}

export async function submitRiskAssessmentForApproval(id: string): Promise<EEHARiskAssessment> {
  const response = await axios.post(`/api/eeha/risk-assessments/${id}/submit`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to submit risk assessment for approval');
  }

  return response.data.data;
}

export async function approveRiskAssessment(id: string): Promise<EEHARiskAssessment> {
  const response = await axios.post(`/api/eeha/risk-assessments/${id}/approve`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to approve risk assessment');
  }

  return response.data.data;
}

export async function rejectRiskAssessment(id: string, reason: string): Promise<EEHARiskAssessment> {
  const response = await axios.post(
    `/api/eeha/risk-assessments/${id}/reject`,
    { reason }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to reject risk assessment');
  }

  return response.data.data;
}

export async function calculateRiskScore(factors: EEHARiskFactors): Promise<RiskCalculationResult> {
  const response = await axios.post('/api/eeha/risk-assessments/calculate', { factors });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to calculate risk score');
  }

  return response.data.data;
}

export async function deleteRiskAssessment(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/risk-assessments/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete risk assessment');
  }
}

export async function reassessRiskAssessment(id: string): Promise<EEHARiskAssessment> {
  const response = await axios.post(`/api/eeha/risk-assessments/${id}/reassess`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create reassessment');
  }

  return response.data.data;
}

export async function getAssessmentVersions(id: string): Promise<EEHARiskAssessment[]> {
  const response = await axios.get(`/api/eeha/risk-assessments/${id}/versions`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get assessment versions');
  }

  return response.data.data;
}

export function getRiskRatingColor(rating: RiskRating): string {
  const colors = {
    High: '#ef4444',    // red-500
    Medium: '#f59e0b',  // amber-500
    Low: '#22c55e',     // green-500
  };
  return colors[rating] || '#9ca3af';
}

export function getRiskRatingBgColor(rating: RiskRating): string {
  const colors = {
    High: '#fef2f2',    // red-50
    Medium: '#fffbeb',  // amber-50
    Low: '#f0fdf4',     // green-50
  };
  return colors[rating] || '#f9fafb';
}

export function getAssessmentTypeLabel(type: AssessmentType): string {
  const labels = {
    Initial: 'Initial Assessment',
    Periodic: 'Periodic Review',
    PostIncident: 'Post-Incident Review',
  };
  return labels[type] || type;
}

export function getAssessmentStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Draft: '#6b7280',          // gray-500
    PendingApproval: '#3b82f6', // blue-500
    Approved: '#22c55e',        // green-500
    Superseded: '#9ca3af',      // gray-400
  };
  return colors[status] || '#9ca3af';
}

// ============================================================================
// Sample API (Sampling Strategy)
// ============================================================================

export async function getSampleList(
  filters?: EEHASampleFilters
): Promise<{ samples: EEHASample[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    lotId: filters?.lotId,
    sampleType: filters?.sampleType,
    status: filters?.status,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/samples${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get samples');
  }

  return response.data.data;
}

export async function getSample(id: string): Promise<EEHASample> {
  const response = await axios.get(`/api/eeha/samples/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get sample');
  }

  return response.data.data;
}

export async function getSampleProgress(id: string): Promise<SampleProgress> {
  const response = await axios.get(`/api/eeha/samples/${id}/progress`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get sample progress');
  }

  return response.data.data;
}

export async function generateSample(options: SampleGenerationOptions): Promise<EEHASample> {
  try {
    const response = await axios.post('/api/eeha/samples/generate', options);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to generate sample');
    }

    return response.data.data;
  } catch (err: unknown) {
    // Extract actual backend error message from Axios error responses
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const backendMsg = axiosErr.response?.data?.error;
      if (backendMsg) {
        throw new Error(backendMsg);
      }
    }
    throw err;
  }
}

export async function activateSample(id: string): Promise<EEHASample> {
  const response = await axios.post(`/api/eeha/samples/${id}/activate`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to activate sample');
  }

  return response.data.data;
}

export async function completeSample(id: string): Promise<EEHASample> {
  const response = await axios.post(`/api/eeha/samples/${id}/complete`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to complete sample');
  }

  return response.data.data;
}

export async function generateRetestSample(
  sampleId: string,
  options?: { retestPercentage?: number }
): Promise<EEHASample> {
  const response = await axios.post(`/api/eeha/samples/${sampleId}/retest`, options || {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to generate retest sample');
  }

  return response.data.data;
}

export async function updateSampleItem(
  sampleId: string,
  tagId: string,
  data: {
    inspectionId?: string;
    inspectionNo?: string;
    result?: InspectionResult;
    skippedReason?: string;
  }
): Promise<EEHASample> {
  const response = await axios.put(`/api/eeha/samples/${sampleId}/items/${tagId}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update sample item');
  }

  return response.data.data;
}

export async function deleteSample(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/samples/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete sample');
  }
}

export async function getSamplesByLot(
  lotId: string,
  filters?: { status?: string }
): Promise<EEHASample[]> {
  const queryParams = buildQueryString({ status: filters?.status });
  const response = await axios.get(`/api/eeha/samples/by-lot/${lotId}${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get samples for lot');
  }

  return response.data.data;
}

export async function calculateSampleSize(
  lotSize: number,
  options?: {
    percentage?: number;
    minSize?: number;
    maxSize?: number;
    confidenceLevel?: number;
    marginOfError?: number;
  }
): Promise<{ sampleSize: number; method: string }> {
  const response = await axios.post('/api/eeha/samples/calculate-size', { lotSize, ...options });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to calculate sample size');
  }

  return response.data.data;
}

export function getSampleTypeLabel(type: SampleType): string {
  const labels = {
    Random: 'Random Selection',
    Stratified: 'Stratified Sampling',
    Targeted: 'Targeted Selection',
  };
  return labels[type] || type;
}

export function getSampleStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Draft: '#6b7280',          // gray-500
    Active: '#3b82f6',         // blue-500
    Completed: '#22c55e',      // green-500
    RetestRequired: '#f59e0b', // amber-500
  };
  return colors[status] || '#9ca3af';
}

export function getSampleItemStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Pending: '#6b7280',    // gray-500
    Completed: '#22c55e',  // green-500
    Skipped: '#9ca3af',    // gray-400
  };
  return colors[status] || '#9ca3af';
}

export function getInspectionResultColor(result: InspectionResult | string): string {
  const colors: Record<string, string> = {
    Pass: '#22c55e',             // green-500
    'Conditional Pass': '#f59e0b', // amber-500
    Fail: '#ef4444',             // red-500
  };
  return colors[result] || '#9ca3af';
}

// ============================================================================
// Workpack API (Offline Capability)
// ============================================================================


export async function getWorkpackList(
  filters?: EEHAWorkpackFilters
): Promise<{ workpacks: EEHAWorkpack[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    sourceType: filters?.sourceType,
    status: filters?.status,
    assignedToEmail: filters?.assignedToEmail,
    sampleId: filters?.sampleId,
    syncStatus: filters?.syncStatus,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });

  const response = await axios.get(`/api/eeha/workpacks${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get workpack list');
  }

  return response.data.data;
}

export async function getMyWorkpacks(
  filters?: { status?: string; syncStatus?: string }
): Promise<EEHAWorkpack[]> {
  const queryParams = buildQueryString(filters);
  const response = await axios.get(`/api/eeha/workpacks/my${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get my workpacks');
  }

  return response.data.data;
}

export async function getWorkpack(id: string): Promise<EEHAWorkpack> {
  const response = await axios.get(`/api/eeha/workpacks/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get workpack');
  }

  return response.data.data;
}

export async function getWorkpackEnriched(id: string): Promise<EEHAEnrichedWorkpack> {
  const response = await axios.get(`/api/eeha/workpacks/${id}/enriched`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get enriched workpack');
  }

  return response.data.data;
}

export async function createWorkpackFromSample(data: {
  sampleId: string;
  name: string;
  description?: string;
  scheduledDate: string;
  dueDate: string;
  assignedToEmail: string;
}): Promise<EEHAWorkpack> {
  const response = await axios.post('/api/eeha/workpacks/from-sample', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create workpack');
  }

  return response.data.data;
}

export async function createAdHocWorkpack(data: {
  clientName: string;
  siteName: string;
  plantName?: string;
  name: string;
  description?: string;
  scheduledDate: string;
  dueDate: string;
  assignedToEmail: string;
  tagIds: string[];
}): Promise<EEHAWorkpack> {
  const response = await axios.post('/api/eeha/workpacks', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create workpack');
  }

  return response.data.data;
}

export async function createWorkpackFromDefects(
  defectIds: string[],
  options?: { name?: string; assignedToEmail?: string; scheduledDate?: string; dueDate?: string }
): Promise<EEHAWorkpack> {
  const response = await axios.post('/api/eeha/workpacks/from-defects', {
    defectIds,
    ...options,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create workpack from defects');
  }

  return response.data.data;
}

export async function updateWorkpack(id: string, data: Partial<EEHAWorkpack>): Promise<EEHAWorkpack> {
  const response = await axios.put(`/api/eeha/workpacks/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update workpack');
  }

  return response.data.data;
}

export async function downloadWorkpack(
  id: string,
  deviceId?: string,
  appVersion?: string
): Promise<WorkpackDownloadBundle> {
  const queryParams = buildQueryString({ deviceId, appVersion });
  const response = await axios.get(`/api/eeha/workpacks/${id}/download${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to download workpack');
  }

  return response.data.data;
}

export async function startWorkpack(id: string): Promise<EEHAWorkpack> {
  const response = await axios.post(`/api/eeha/workpacks/${id}/start`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to start workpack');
  }

  return response.data.data;
}

export async function completeWorkpack(id: string, notes?: string): Promise<EEHAWorkpack> {
  const response = await axios.post(`/api/eeha/workpacks/${id}/complete`, { notes });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to complete workpack');
  }

  return response.data.data;
}

export async function checkinWorkpack(id: string): Promise<EEHAWorkpack> {
  const response = await axios.post(`/api/eeha/workpacks/${id}/checkin`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to check in workpack');
  }

  return response.data.data;
}

export async function updateWorkpackItem(
  workpackId: string,
  tagId: string,
  data: {
    status?: string;
    inspectionId?: string;
    inspectionNo?: string;
    result?: string;
    skippedReason?: string;
    offlineData?: Record<string, unknown>;
  }
): Promise<EEHAWorkpack> {
  const response = await axios.put(`/api/eeha/workpacks/${workpackId}/items/${tagId}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update workpack item');
  }

  return response.data.data;
}

export async function deleteWorkpack(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/workpacks/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete workpack');
  }
}

// Workpack helpers
export function getWorkpackStatusColor(status: WorkpackStatus | string): string {
  const colors: Record<string, string> = {
    Draft: '#6b7280',        // gray-500
    Assigned: '#3b82f6',     // blue-500
    InProgress: '#f59e0b',   // amber-500
    Completed: '#22c55e',    // green-500
    Cancelled: '#ef4444',    // red-500
  };
  return colors[status] || '#9ca3af';
}

export function getWorkpackSourceTypeLabel(type: WorkpackSourceType | string): string {
  const labels: Record<string, string> = {
    Sample: 'From Sample',
    Scheduled: 'Scheduled',
    AdHoc: 'Ad-Hoc',
  };
  return labels[type] || type;
}

export function getSyncStatusColor(status: SyncStatus | string): string {
  const colors: Record<string, string> = {
    NotDownloaded: '#6b7280',  // gray-500
    Downloaded: '#3b82f6',     // blue-500
    PartialSync: '#f59e0b',    // amber-500
    FullySync: '#22c55e',      // green-500
  };
  return colors[status] || '#9ca3af';
}

export function getSyncStatusLabel(status: SyncStatus | string): string {
  const labels: Record<string, string> = {
    NotDownloaded: 'Not Downloaded',
    Downloaded: 'Downloaded',
    PartialSync: 'Partial Sync',
    FullySync: 'Fully Synced',
  };
  return labels[status] || status;
}

// ============================================================================
// Offline Sync API
// ============================================================================


export async function getOfflineSyncStatus(deviceId?: string): Promise<EEHAOfflineSyncStatus> {
  const queryParams = buildQueryString({ deviceId });
  const response = await axios.get(`/api/eeha/offline/status${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get sync status');
  }

  return response.data.data;
}

export async function queueOfflineItem(data: {
  deviceId: string;
  entityType: OfflineEntityType;
  entityId?: string;
  operation: OfflineOperation;
  clientName: string;
  siteName: string;
  plantName?: string;
  isAdHoc?: boolean;
  tagNo?: string;
  tagId?: string;
  workpackId?: string;
  workpackNo?: string;
  offlineCreatedAt?: string;
  offlineData: Record<string, unknown>;
  checksum?: string;
  appVersion?: string;
}): Promise<EEHAOfflineQueueItem> {
  const response = await axios.post('/api/eeha/offline/queue', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to queue offline item');
  }

  return response.data.data;
}

export async function getPendingOfflineItems(
  deviceId?: string,
  limit?: number
): Promise<EEHAOfflineQueueItem[]> {
  const queryParams = buildQueryString({ deviceId, limit });
  const response = await axios.get(`/api/eeha/offline/pending${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get pending items');
  }

  return response.data.data;
}

export async function syncOfflineItem(queueId: string): Promise<{
  queueItem: EEHAOfflineQueueItem;
  syncedEntityId?: string;
}> {
  const response = await axios.post(`/api/eeha/offline/sync/${queueId}`, {});

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to sync item');
  }

  return {
    queueItem: response.data.data,
    syncedEntityId: response.data.syncedEntityId,
  };
}

export async function syncOfflineBatch(options: {
  queueIds?: string[];
  deviceId?: string;
  limit?: number;
}): Promise<{
  results: EEHASyncBatchResult;
  summary: EEHASyncBatchSummary;
}> {
  const response = await axios.post('/api/eeha/offline/sync-batch', options);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to sync batch');
  }

  return {
    results: response.data.data,
    summary: response.data.summary,
  };
}

export async function getOfflineConflicts(): Promise<EEHAOfflineQueueItem[]> {
  const response = await axios.get('/api/eeha/offline/conflicts');

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get conflicts');
  }

  return response.data.data;
}

export async function resolveOfflineConflict(
  queueId: string,
  resolution: ConflictResolution,
  mergedData?: Record<string, unknown>
): Promise<EEHAOfflineQueueItem> {
  const response = await axios.post(`/api/eeha/offline/conflicts/${queueId}/resolve`, {
    resolution,
    mergedData,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to resolve conflict');
  }

  return response.data.data;
}

export async function createAdHocInspection(data: {
  deviceId?: string;
  clientName: string;
  siteName: string;
  plantName?: string;
  tagNo: string;
  inspectionData: Record<string, unknown>;
  offlineCreatedAt?: string;
  appVersion?: string;
}): Promise<{ queueItem: EEHAOfflineQueueItem; tagFound: boolean }> {
  const response = await axios.post('/api/eeha/offline/adhoc-inspection', data);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create ad-hoc inspection');
  }

  return {
    queueItem: response.data.data,
    tagFound: response.data.tagFound,
  };
}

export async function cleanupSyncedItems(olderThanDays?: number): Promise<{ deletedCount: number }> {
  const response = await axios.post('/api/eeha/offline/cleanup', { olderThanDays });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to cleanup synced items');
  }

  return response.data.data;
}

// Offline helpers
export function getSyncItemStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Pending: '#6b7280',    // gray-500
    Syncing: '#3b82f6',    // blue-500
    Synced: '#22c55e',     // green-500
    Conflict: '#f59e0b',   // amber-500
    Failed: '#ef4444',     // red-500
  };
  return colors[status] || '#9ca3af';
}

export function getSyncItemStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    Pending: 'Pending Sync',
    Syncing: 'Syncing...',
    Synced: 'Synced',
    Conflict: 'Conflict',
    Failed: 'Failed',
  };
  return labels[status] || status;
}

// ============================================================================
// Export/Import API
// ============================================================================

export interface ExportScope {
  clientName: string;
  siteName?: string;
  plantName?: string;
}

export interface ExportCounts {
  tags: number;
  equipment: number;
  inspections: number;
  defects: number;
  documents: number;
  sitemaps: number;
}

export type ExportDataType = 'tags' | 'equipment' | 'inspections' | 'defects' | 'documents' | 'sitemaps';

export interface ExportManifest {
  version: string;
  exportDate: string;
  exportedBy: string;
  scope: ExportScope;
  counts: ExportCounts;
}

export interface ImportPreviewResult {
  manifest: ExportManifest;
  conflicts: Array<{ type: string; identifier: string; existingId: string }>;
  fileSize: number;
}

export interface ImportResult {
  [key: string]: {
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  };
}

export async function getExportCounts(
  scope: ExportScope
): Promise<{ scope: ExportScope; counts: ExportCounts }> {
  const queryParams = buildQueryString({
    clientName: scope.clientName,
    siteName: scope.siteName,
    plantName: scope.plantName,
  });

  const response = await axios.get(`/api/eeha/export/counts${queryParams}`);

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get export counts');
  }

  return response.data.data;
}

export async function exportData(
  scope: ExportScope,
  dataTypes: ExportDataType[]
): Promise<Blob> {
  const response = await axios.post(
    `/api/eeha/export`,
    { scope, dataTypes },
    {
      responseType: 'blob',
    }
  );

  return response.data;
}

export async function previewImport(file: File): Promise<ImportPreviewResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`/api/eeha/export/import/preview`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to preview import');
  }

  return response.data.data;
}

export async function executeImport(
  file: File,
  targetScope: ExportScope,
  mode: 'merge' | 'replace',
  skipConflicts: boolean
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('targetScope', JSON.stringify(targetScope));
  formData.append('mode', mode);
  formData.append('skipConflicts', String(skipConflicts));

  const response = await axios.post(`/api/eeha/export/import/execute`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to execute import');
  }

  return response.data.data;
}

// ============================================================================
// Gap 1: Result Options
// ============================================================================

export async function getResultOptions(
  inspectionType: string,
  clientName?: string
): Promise<string[]> {
  const queryParams = buildQueryString({ inspectionType, clientName });
  const response = await axios.get(`/api/eeha-config/result-options${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get result options');
  }
  return response.data.data;
}

// ============================================================================
// Gap 2: Fault Categories
// ============================================================================

export async function getFaultCategories(
  protectionType?: string
): Promise<EEHAFaultCategory[]> {
  const queryParams = buildQueryString({ protectionType });
  const response = await axios.get(`/api/eeha-config/fault-categories${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get fault categories');
  }
  return response.data.data;
}

// ============================================================================
// Scoped Config (Site/Client-level configuration)
// ============================================================================

export async function getResolvedScopedConfig(
  clientName: string,
  siteName?: string
): Promise<EEHAResolvedConfig> {
  const queryParams = buildQueryString({ clientName, siteName });
  const response = await axios.get(`/api/eeha-config/scoped-resolve${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get resolved config');
  }
  return response.data.data;
}

export async function getScopedConfig(
  clientName: string,
  siteName?: string
): Promise<EEHAScopedConfig | null> {
  const queryParams = buildQueryString({ clientName, siteName });
  const response = await axios.get(`/api/eeha-config/scoped${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get scoped config');
  }
  return response.data.data;
}

export async function updateScopedConfig(
  clientName: string,
  siteName: string | undefined,
  payload: Partial<Pick<EEHAScopedConfig, 'options' | 'faultCategories' | 'certificationNotes'>>
): Promise<EEHAScopedConfig> {
  const queryParams = buildQueryString({ clientName, siteName });
  const response = await axios.put(`/api/eeha-config/scoped${queryParams}`, payload);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update scoped config');
  }
  return response.data.data;
}

export async function copyScopedConfigToSite(
  clientName: string,
  siteName: string
): Promise<EEHAScopedConfig> {
  const queryParams = buildQueryString({ clientName, siteName });
  const response = await axios.post(`/api/eeha-config/scoped-copy${queryParams}`, {});
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to copy config to site');
  }
  return response.data.data;
}

export async function getScopedConfigHistory(
  clientName: string,
  siteName?: string,
  page = 1,
  limit = 20
): Promise<{ entries: EEHAScopedChangeEntry[]; total: number; page: number; limit: number }> {
  const queryParams = buildQueryString({ clientName, siteName, page, limit });
  const response = await axios.get(`/api/eeha-config/scoped-history${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get config history');
  }
  return response.data.data;
}

// ============================================================================
// Gap 3: Work Order Links
// ============================================================================

export async function getWorkOrderLinks(params: {
  sourceType?: string;
  sourceId?: string;
  clientName?: string;
}): Promise<EEHAWorkOrderLink[]> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/eam/work-orders${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get work order links');
  }
  return response.data.data;
}

// ============================================================================
// Gap 5: Common Faults
// ============================================================================

export async function getCommonFaults(
  itemCode: string
): Promise<{ commonFindings: string[]; suggestedActions: string[] }> {
  const response = await axios.get(`/api/eeha-config/common-faults/${encodeURIComponent(itemCode)}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get common faults');
  }
  return response.data.data;
}

// ============================================================================
// Gap 6: Sample Escalation Chain
// ============================================================================

export async function getSampleEscalationChain(sampleId: string): Promise<EEHASample[]> {
  const response = await axios.get(`/api/eeha/samples/${sampleId}/escalation-chain`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get escalation chain');
  }
  return response.data.data;
}

// ============================================================================
// Gap 7 & 11: Report Downloads
// ============================================================================

export async function downloadDefectReport(params: {
  clientName?: string;
  siteName?: string;
  plantName?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/report/defect-summary${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function downloadComplianceReport(params: {
  clientName?: string;
  siteName?: string;
  plantName?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/report/compliance${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function downloadCampaignReport(params: {
  clientName?: string;
  siteName?: string;
  workpackIds?: string[];
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/report/campaign${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportDefectsExcel(params: {
  clientName?: string;
  siteName?: string;
  plantName?: string;
  status?: string;
  severity?: string;
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/excel/defects${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportInspectionsExcel(params: {
  clientName?: string;
  siteName?: string;
  plantName?: string;
  status?: string;
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/excel/inspections${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportTagsExcel(params: {
  clientName?: string;
  siteName?: string;
  plantName?: string;
}): Promise<Blob> {
  const queryParams = buildQueryString(params);
  const response = await axios.get(`/api/eeha/export/excel/tags${queryParams}`, {
    responseType: 'blob',
  });
  return response.data;
}

// ============================================================================
// Gap 12: Campaign CRUD
// ============================================================================

export async function getCampaignList(
  filters?: EEHACampaignFilters
): Promise<{ campaigns: EEHACampaign[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const queryParams = buildQueryString({
    search: filters?.search,
    clientName: filters?.clientName,
    siteName: filters?.siteName,
    plantName: filters?.plantName,
    campaignType: filters?.campaignType,
    status: filters?.status,
    page: filters?.page,
    limit: filters?.limit,
    sortBy: filters?.sortBy,
    sortDir: filters?.sortDir,
  });
  const response = await axios.get(`/api/eeha/campaigns${queryParams}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get campaign list');
  }
  return response.data.data;
}

export async function getCampaign(id: string): Promise<EEHACampaign> {
  const response = await axios.get(`/api/eeha/campaigns/${id}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to get campaign');
  }
  return response.data.data;
}

export async function createCampaign(data: Partial<EEHACampaign>): Promise<EEHACampaign> {
  const response = await axios.post('/api/eeha/campaigns', data);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create campaign');
  }
  return response.data.data;
}

export async function updateCampaign(id: string, data: Partial<EEHACampaign>): Promise<EEHACampaign> {
  const response = await axios.put(`/api/eeha/campaigns/${id}`, data);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to update campaign');
  }
  return response.data.data;
}

export async function deleteCampaign(id: string): Promise<void> {
  const response = await axios.delete(`/api/eeha/campaigns/${id}`);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete campaign');
  }
}

export default {
  // Equipment
  getEquipmentList,
  getEquipment,
  createEquipment,
  updateEquipment,
  deleteEquipment,
  generateEquipmentQR,
  scanEquipmentQR,
  getEquipmentStats,
  getEquipmentInspections,
  getEquipmentDefects,
  getEquipmentFilterOptions,

  // Inspection
  getInspectionList,
  getInspection,
  createInspection,
  updateInspection,
  deleteInspection,
  submitInspection,
  approveInspection,
  rejectInspection,
  uploadInspectionFile,
  deleteInspectionFile,
  renderInspectionPdf,
  getInspectionDashboardStats,
  getInspectionFilterOptions,

  // Defect
  getDefectList,
  getDefect,
  createDefect,
  updateDefect,
  assignDefect,
  remediateDefect,
  verifyDefect,
  closeDefect,
  deferDefect,
  uploadDefectPhoto,
  deleteDefectPhoto,
  getOverdueDefects,
  getDefectSummary,
  getDefectFilterOptions,

  // Config
  getConfig,
  updateConfig,
  getDefaultChecklist,
  getConfigOptions,
  getSLADefaults,
  getClientSiteHierarchy,

  // User Permissions
  getMyPermissions,

  // Client Config
  getClientConfig,
  updateClientConfig,
  addClientContact,
  updateClientContact,
  deleteClientContact,
  addSiteOverride,
  updateSiteOverride,
  deleteSiteOverride,

  // Utilities
  getSeverityColor,
  getStatusColor,
  getResultColor,
  getZoneDescription,
  formatSLADays,
  calculateDaysUntilDue,

  // Tag (Location Registry)
  getTagList,
  getTag,
  createTag,
  updateTag,
  deleteTag,
  assignEquipmentToTag,
  removeEquipmentFromTag,
  generateTagQR,
  scanTagQR,
  getTagHistory,
  getTagFilterOptions,
  bulkImportTags,
  downloadTagImportTemplate,

  // Site Map
  getSiteMapList,
  getSiteMapsBySite,
  getSiteMap,
  getSiteMapWithTags,
  createSiteMap,
  updateSiteMap,
  updateSiteMapFile,
  deleteSiteMap,
  addTagToMap,
  updateTagPositionOnMap,
  removeTagFromMap,
  addZoneToMap,
  updateZoneOnMap,
  deleteZoneFromMap,
  addLabelToMap,
  updateLabelOnMap,
  deleteLabelFromMap,
  getTrafficLightColor,
  getTrafficLightDescription,

  // Lot (Risk-Based Inspection Grouping)
  getLotList,
  getLot,
  createLot,
  updateLot,
  deleteLot,
  getLotTags,
  addTagsToLot,
  removeTagFromLot,
  getLotCompliance,
  recalculateLotStats,
  getLotFilterOptions,
  getLotCriteriaOptions,
  getRiskCategoryColor,
  getRiskCategoryBgColor,
  getMembershipTypeLabel,

  // Risk Assessment (RBI)
  getRiskAssessmentList,
  getRiskAssessment,
  createRiskAssessment,
  updateRiskAssessment,
  submitRiskAssessmentForApproval,
  approveRiskAssessment,
  rejectRiskAssessment,
  calculateRiskScore,
  deleteRiskAssessment,
  reassessRiskAssessment,
  getAssessmentVersions,
  getRiskRatingColor,
  getRiskRatingBgColor,
  getAssessmentTypeLabel,
  getAssessmentStatusColor,

  // Sampling
  getSampleList,
  getSample,
  getSampleProgress,
  generateSample,
  activateSample,
  completeSample,
  generateRetestSample,
  updateSampleItem,
  deleteSample,
  getSamplesByLot,
  calculateSampleSize,
  getSampleTypeLabel,
  getSampleStatusColor,
  getSampleItemStatusColor,
  getInspectionResultColor,

  // Workpack (Offline Capability)
  getWorkpackList,
  getMyWorkpacks,
  getWorkpack,
  getWorkpackEnriched,
  createWorkpackFromSample,
  createAdHocWorkpack,
  updateWorkpack,
  downloadWorkpack,
  startWorkpack,
  completeWorkpack,
  checkinWorkpack,
  updateWorkpackItem,
  deleteWorkpack,
  getWorkpackStatusColor,
  getWorkpackSourceTypeLabel,
  getSyncStatusColor,
  getSyncStatusLabel,

  // Offline Sync
  getOfflineSyncStatus,
  queueOfflineItem,
  getPendingOfflineItems,
  syncOfflineItem,
  syncOfflineBatch,
  getOfflineConflicts,
  resolveOfflineConflict,
  createAdHocInspection,
  cleanupSyncedItems,
  getSyncItemStatusColor,
  getSyncItemStatusLabel,

  // Export/Import
  getExportCounts,
  exportData,
  previewImport,
  executeImport,

  // Gap Closure APIs
  getResultOptions,
  getFaultCategories,
  getWorkOrderLinks,
  getCommonFaults,
  getSampleEscalationChain,
  downloadDefectReport,
  downloadComplianceReport,
  downloadCampaignReport,
  exportDefectsExcel,
  exportInspectionsExcel,
  exportTagsExcel,
  getCampaignList,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,

  // Inspection Approvers
  getClientInspectionApprovers,
  getEligibleApprovers,
  addClientInspectionApprover,
  updateClientInspectionApprover,
  deleteClientInspectionApprover,

  // Scoped Config
  getResolvedScopedConfig,
  getScopedConfig,
  updateScopedConfig,
  copyScopedConfigToSite,
  getScopedConfigHistory,
};
